import { useEffect, useMemo, useState } from 'react';
import { Modal, Box, Typography, Paper } from '@mui/material';
import dayjs, { type Dayjs } from 'dayjs';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import useCustomerInfo from 'hooks/useCustomerInfo';
import CustomDatePicker from 'components/DatePIcker/CustomDatePicker';
import { useTranslation } from 'react-i18next';

export const PreviewDateSelectorModal = ({
  isOpen,
  onClose,
  onApply,
  date,
  selectedDateRange,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedDateRange: string;
  date: dayjs.Dayjs | null;
  onApply: (
    paramSelectedRangeLstring: string,
    paramSelectedDate: Dayjs
  ) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { customerTz } = useCustomerInfo();

  const { todayInTz, yesterdayInTz } = useMemo(() => {
    return {
      yesterdayInTz: dayjs().tz(customerTz).subtract(1, 'day'),
      todayInTz: dayjs().tz(customerTz),
    };
  }, [customerTz]);

  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  const handleApplyClick = async () => {
    try {
      if (!selectedDate) return;
      const selectedStartOfDay = selectedDate.tz(customerTz).startOf('day');
      let rangeToSend: string;
      if (selectedStartOfDay.isSame(todayInTz, 'day')) {
        rangeToSend = 'Today';
      } else if (selectedStartOfDay.isSame(yesterdayInTz, 'day')) {
        rangeToSend = '1D';
      } else {
        rangeToSend = 'Custom';
      }
      await onApply(rangeToSend, selectedDate);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (selectedDateRange === 'Today') {
      setSelectedDate(todayInTz);
    } else if (selectedDateRange === '1D') {
      setSelectedDate(yesterdayInTz);
    } else {
      if (date) {
        setSelectedDate(date);
      } else {
        setSelectedDate(yesterdayInTz);
      }
    }
  }, []);

  return (
    <Modal
      open={isOpen}
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown' || reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper
        sx={{
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
          padding: '24px',
          minWidth: '340px',
          outline: 'none',
        }}
        elevation={4}
      >
        <Typography
          sx={{
            fontSize: '18px',
            fontWeight: 600,
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          {t('select_date_for_preview')}
        </Typography>

        <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
          {customerTz ? (
            <CustomDatePicker
              value={selectedDate}
              onChange={(value) => {
                if (value) {
                  setSelectedDate(value);
                }
              }}
              maxDate={todayInTz}
              timezone={customerTz}
              sx={{
                width: '180px',
                minWidth: '180px',
              }}
            />
          ) : null}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '24px',
            gap: '12px',
          }}
        >
          <CustomLoadingButton onClick={onClose} color="error">
            {t('cancel')}
          </CustomLoadingButton>
          <CustomLoadingButton
            sx={{
              width: '80px',
            }}
            onClick={handleApplyClick}
          >
            {t('apply')}
          </CustomLoadingButton>
        </Box>
      </Paper>
    </Modal>
  );
};
